import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { LoadingPopup } from 'components/common';
import { DatePickerField, InputFile, Inputs } from 'components/form-control';
import { TOAST } from 'config';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { CREATE_OR_EDIT_CHILDREN_PARENT_DETAIL_BODY } from 'models';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  createParentChildrenDetailApi,
  editParentChildrenDetailApi,
  getParentChildrenDetailApi,
  updateImageApi,
} from 'services';

interface Props {
  open: boolean;
  subject?: string;
  idChildren?: string;
  handleClosePopup: () => void;
  onSuccess: () => void;
}

interface IFormInputs {
  date_of_birth: string | null;
  full_name: string;
  picture: string;
}

const defaultValues: Partial<IFormInputs> = {
  date_of_birth: null,
  full_name: '',
  picture: '',
};

const PopupCreateOrEditChildren = (props: Props) => {
  const { open, handleClosePopup, subject, onSuccess, idChildren } = props;

  const [nameIcon, setNameIcon] = useState<string>();
  const [urlImage, setUrlImage] = useState<any>();
  const [loadingPopup, setLoadingPopup] = useState(false);

  const schema = useMemo(() => {
    return yup.object().shape({
      date_of_birth: yup.mixed().required('Date of birth is required'),
      full_name: yup
        .string()
        .max(50, 'Maximum of 50 characters')
        .required('Children Name is required'),
    });
  }, []);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const { reset, handleSubmit, watch } = form;

  const numberCount = watch('full_name')?.length;

  const _handleClosePopup = () => {
    reset({ ...defaultValues });
    handleClosePopup();
  };

  useEffect(() => {
    if (idChildren && subject) {
      setLoadingPopup(true);
      const getDetail = async () => {
        await getParentChildrenDetailApi(subject, idChildren)
          .then((r) => {
            reset({
              full_name: r.data.data.full_name,
              date_of_birth: r.data.data.date_of_birth,
              picture: r.data.data.picture,
            });
            setUrlImage(r.data.data.picture);
            const nameIcon =
              r.data.data.picture.split('/')[
                r.data.data.picture.split('/').length - 1
              ];

            setNameIcon(nameIcon);
            setLoadingPopup(false);
          })
          .catch((e) => NotifyService.error(e))
          .finally(() => {});
      };
      getDetail();
    }
  }, [idChildren, subject, reset]);

  const onSubmit = async (value: IFormInputs) => {
    if (!subject) return;
    const data: CREATE_OR_EDIT_CHILDREN_PARENT_DETAIL_BODY = {
      full_name: value.full_name,
      date_of_birth: value.date_of_birth,
      picture: urlImage,
    };
    if (idChildren) {
      await editParentChildrenDetailApi(subject, idChildren, data)
        .then((r) => {
          if (r.data.message === 'Success') {
            onSuccess();
            _handleClosePopup();
            NotifyService.success(r);
          }
        })
        .catch((e) => NotifyService.error(e))
        .finally(() => {});
    } else {
      await createParentChildrenDetailApi(subject, data)
        .then((r) => {
          if (r.data.message === 'Success') {
            onSuccess();
            _handleClosePopup();
            NotifyService.success(r);
          }
        })
        .catch((e) => NotifyService.error(e))
        .finally(() => {});
    }
  };

  const handleChangeIcon = (e: any, _: any) => {
    if (e?.target?.files === null) return;
    const files = e?.target?.files[0];
    const formData = new FormData();
    formData.append('image', files);
    const fetchData = async () => {
      await updateImageApi(formData)
        .then((r) => {
          if (r.data.message === 'Success') {
            setUrlImage(r.data.data);
          }
        })
        .catch(() => {
          toast.error('Upload Image Failed', TOAST.error);
        })
        .finally(() => {});
    };
    fetchData();
    setNameIcon(files.name);
    e.target.value = null;
  };

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 523,
          height: 'auto',
          borderRadius: 0,
        },
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 25px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '100%',
          }}
        >
          {idChildren ? 'Edit' : 'Create'} Children
        </Typography>
        <IconButton
          onClick={_handleClosePopup}
          sx={{
            padding: 0,
          }}
        >
          <CloseCircle />
        </IconButton>
      </Grid>
      <Divider />
      {loadingPopup && <LoadingPopup color="primary" />}
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Stack
          p="30px 24px 50px 26px"
          height="100%"
          justifyContent="space-between"
        >
          <Stack mb="20px">
            <InputFile
              form={form}
              nameFile={nameIcon}
              name="picture"
              title="Profile Photo"
              accept="image/*"
              handleChange={handleChangeIcon}
              sx={{
                width: 118,
                height: 34,
                boxSizing: 'border-box',
              }}
            />
          </Stack>
          {urlImage && (
            <Box
              mb="20px"
              width="109px"
              height="109px"
              sx={{ position: 'relative' }}
            >
              <div
                style={{
                  position: 'absolute',
                  zIndex: 2,
                  background: '#302d2d6b',
                  width: '109px',
                  height: '109px',
                }}
              />
              <img src={urlImage} alt="" width="109" height="109" />
            </Box>
          )}
          <Box mb="20px">
            <Inputs
              title="What’s your child’s name?"
              form={form}
              name="full_name"
              showCount={true}
              numberCount={numberCount}
              placeholder={'Child’s name'}
            />
          </Box>
          <Stack
            sx={{
              '& input': {
                '::placeholder': {
                  textTransform: 'uppercase',
                },
              },
              '& .button-calendar': {
                width: '100% !important',
              },
            }}
          >
            <DatePickerField
              title="How old is your child?"
              form={form}
              name="date_of_birth"
              inputFormat="DD MMM YYYY"
              maxDate={moment()}
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between" mt="25px">
            <Button
              variant="outlined"
              color="secondary"
              sx={{
                width: '233.5px',
                height: '30px',
              }}
              onClick={_handleClosePopup}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              sx={{
                width: '233.5px',
                height: '30px',
              }}
              type="submit"
            >
              {idChildren ? 'Update' : 'Create'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  );
};

export default PopupCreateOrEditChildren;
